import type { AccountNode } from '@taler/api-client'
import { type Locale, resolveLocalized } from '@taler/domain'

export interface SourceAccountOption {
  readonly id: string
  readonly label: string
  readonly currency: string | undefined
}

/**
 * A loan money account: a postable asset or liability leaf, the same money accounts entries
 * post against, restricted to the loan's own currency (a payment's source may be an asset or liability; a drawdown's destination is an asset) - a payment posted from an account in a
 * different currency would produce a booking whose source posting doesn't match that account's
 * own currency, and the server has no conversion step to reconcile it.
 */
export function flattenSourceAccounts(
  nodes: readonly AccountNode[],
  currency: string,
  locale: Locale,
  classes: readonly string[] = ['ASSET', 'LIABILITY'],
): readonly SourceAccountOption[] {
  const out: SourceAccountOption[] = []
  const walk = (node: AccountNode): void => {
    const { account } = node
    if (account.isPostable && classes.includes(account.accountClass) && account.currency === currency) {
      out.push({
        id: account.id,
        label: resolveLocalized(account.names, locale)?.name ?? account.code,
        currency: account.currency,
      })
    }
    for (const child of node.children) walk(child)
  }
  for (const node of nodes) walk(node)
  return out
}
